import { memo, useContext } from 'react';
import type { ReactNode } from 'react';
import useTitle from 'react-use/lib/useTitle';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Dropdown from 'react-bootstrap/Dropdown';
import MenuItem from 'react-bootstrap/DropdownItem';

import APPConf from '../../../lib/config/common';
import { Form, FormContext, SubmitButton } from '../components/forms';
import { Button } from '../components/buttons';
import Icon from '../components/Icon';

/** Пропсы внутреннего FormContent (шапка страницы-формы + кнопки). */
type FormContentProps = {
  loading?: boolean;
  header?: ReactNode;
  /** Строка для title вкладки браузера — когда header является JSX-элементом. */
  title?: string;
  deleteButtonTitle?: string;
  submitButtonTitle?: string;
  showDeleteButton?: boolean;
  showSubmitButton?: boolean;
  /** Не отрисовывать заголовок формы. Понадобился для встраивания формы целиком в другую форму (Permission). */
  showHeaderRow?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onReload?: (...args: unknown[]) => unknown;
  onDelete?: (...args: unknown[]) => unknown;
  redSubmitButtonIfError?: boolean;
  ignoreDirtyOnSubmit?: boolean;
  validationSchema?: any;
};

const FormContent = (props: FormContentProps) => {
  // FormContext (forms/utils.ts): типизирован как FormContextType|undefined,
  // приводим к ожидаемой форме (Provider подаёт Formik-контекст).
  const formContext = useContext(FormContext) as {
    values: any;
    dirty?: boolean;
    isSubmitting?: boolean;
    isValid?: boolean;
    [key: string]: any;
  };
  const {
    loading,
    header,
    title, // строка для title вкладки браузера — когда header является JSX-элементом
    deleteButtonTitle = 'Удалить',
    submitButtonTitle = 'Сохранить',
    showDeleteButton = false,
    showSubmitButton = true,
    showHeaderRow = true, // не отрисовывать заголовок формы. Понадобился для встраивания формы целиком в другую форму (Permission)
    disableDeleteButton,
    disableSubmitButton,
    children,
    onReload,
    onDelete,
    redSubmitButtonIfError = false,
    ignoreDirtyOnSubmit = false,
  } = props;

  const APPConfName = () => {
    return APPConf.name ? `${ APPConf.name } - ` : '';
  };

  // header бывает JSX-элементом — в title вкладки он превращается в [object Object].
  // Для таких страниц название передаётся отдельным пропом title.
  const documentTitle = typeof header === 'string' ? header : (typeof title === 'string' ? title : '');
  useTitle(documentTitle ? `${ APPConfName() }${ documentTitle }` : (APPConf.name || ''));

  return (
    <>
      { showHeaderRow && (
        <Row className='mb-3'>
          <Col lg={ 8 }>
            <h4 style={ { display: 'inline' } }>
              { header }

              { onReload && (
                <Button
                  variant='link'
                  disabled={ formContext.isSubmitting || loading }
                  onClick={ onReload }
                >
                  <Icon name='refresh' spin={ loading } />
                </Button>
              ) }
            </h4>
          </Col>
          <Col lg={ 4 }>
            <ButtonToolbar className='float-end'>
              { showSubmitButton && !showDeleteButton && (
                <SubmitButton
                  text={ submitButtonTitle }
                  disabled={ disableSubmitButton
                    || (!ignoreDirtyOnSubmit && !formContext.dirty)
                    || formContext.isSubmitting
                    || loading }
                  redSubmitButtonIfError={ redSubmitButtonIfError }
                  isValidValues={ formContext.isValid }
                />
              ) }

              { showSubmitButton && showDeleteButton && (
                <Dropdown as={ ButtonGroup } id='form-actions-submit-delete' disabled={ loading }>
                  <SubmitButton
                    text={ submitButtonTitle }
                    disabled={ disableSubmitButton || !formContext.dirty || formContext.isSubmitting || loading }
                    redSubmitButtonIfError={ redSubmitButtonIfError }
                    isValidValues={ formContext.isValid }
                  />

                  <Dropdown.Toggle variant='primary' />

                  <Dropdown.Menu alignRight>
                    <MenuItem
                      eventKey='1'
                      disabled={ loading || disableDeleteButton }
                      onClick={ onDelete }
                    >
                      <span className='text-danger'>{ deleteButtonTitle }</span>
                    </MenuItem>
                  </Dropdown.Menu>
                </Dropdown>
              ) }
            </ButtonToolbar>
          </Col>
        </Row>
      ) }

      { (typeof children === 'function') ? children(formContext) : children }
    </>
  );
};

/**
 * Пропсы страницы-формы на базе Form: шапка с кнопками сохранения/удаления
 * + document title через react-use/useTitle.
 */
export interface FormPageProps {
  loading?: boolean;
  header?: ReactNode;
  /** Строка для title вкладки, если header — JSX-элемент. */
  title?: string;
  initialValues?: any;
  validationSchema?: any;
  enableReinitialize?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  promptIfDirty?: boolean;
  backRoute?: string;
  backButtonTitle?: string;
  deleteButtonTitle?: string;
  submitButtonTitle?: string;
  showBackButton?: boolean;
  showDeleteButton?: boolean;
  showSubmitButton?: boolean;
  showHeaderRow?: boolean;
  disableBackButton?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onChange?: (prevValues: any, values: any) => void;
  onDelete?: (...args: unknown[]) => unknown;
  onReload?: (...args: unknown[]) => unknown;
  onSubmit?: (values: any, formikHelpers: any) => void | Promise<any>;
  redSubmitButtonIfError?: boolean;
  ignoreDirtyOnSubmit?: boolean;
}

const FormPage = (props: FormPageProps) => {
  const {
    initialValues,
    validationSchema,
    enableReinitialize = true,
    disabled,
    readOnly,
    promptIfDirty,
    // backRoute,
    onChange,
    onSubmit,
    ...rest
  } = props;

  return (
    <Form
      initialValues={ initialValues }
      validationSchema={ validationSchema }
      enableReinitialize={ enableReinitialize }
      readOnly={ readOnly }
      disabled={ disabled }
      promptIfDirty={ promptIfDirty }
      onChange={ onChange }
      onSubmit={ onSubmit }
    >
      <FormContent { ...rest } validationSchema={ validationSchema } />
    </Form>
  );
};

export default memo(FormPage);
